import {
  collection,
  firestore,
  onSnapshot,
  orderBy,
  query,
} from '@ps/functions/firebase/firestore';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { CommunityChannelMessageReport } from '@ps/models/lib/community-channel';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { appDateToIso } from '../utils/appDate';

export interface ChannelMessageReport {
  id: string;
  messageId: string;
  reporterUserId: string;
  reason: string;
  /**
   * The full message snapshot the report was filed against. Stored
   * on the report doc by the server so we don't need to fan out to
   * the messages collection to render the panel.
   */
  message?: ExtendedCommunityChannelMessage;
  createdAt: string;
}

/**
 * Subscribe to a channel's report queue. Reports live at
 * `stores/{creatorId}/communityChannels/{channelId}/reports`, sorted
 * by `createdAt` desc (newest first).
 *
 * Each doc IS the canonical `CommunityChannelMessageReport` (server-written),
 * so we cast `doc.data()` once to that type and read every field typed — no
 * duck typing. The embedded `message` snapshot is the canonical
 * `CommunityChannelMessage`; we wrap it with its `id`/`channelId` for the UI.
 */
export function subscribeToChannelReports(
  creatorId: string,
  channelId: string,
  onChange: (reports: ChannelMessageReport[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = collection(
    db,
    'stores',
    creatorId,
    'communityChannels',
    channelId,
    'reports',
  );
  const q = query(ref, orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(
    q,
    snapshot => {
      const reports: ChannelMessageReport[] = [];
      snapshot.forEach(doc => {
        const data = snapshotToDataWithId<CommunityChannelMessageReport>(doc);
        if (!data) return;
        const message: ExtendedCommunityChannelMessage | undefined =
          data.message
            ? { ...data.message, id: data.messageId, channelId }
            : undefined;
        reports.push({
          id: data.id,
          messageId: data.messageId,
          reporterUserId: data.reporterUserId,
          reason: data.reason,
          message,
          createdAt: appDateToIso(data.createdAt) ?? '',
        });
      });
      onChange(reports);
    },
    error => onError?.(error),
  );
  return unsubscribe;
}
